// src/pages/Notifications/NotificationList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaBell,
  FaCalendarAlt,
  FaClock,
  FaInbox,
  FaSearch,
  FaSync,
  FaTrash,
} from "react-icons/fa";

import {
  getNotificationsByUser,
  deleteNotificationById,
} from "./../../services/notificationServices";

import { useNotification } from "./../../contexts/NotificationContext";
import { useUser } from "./../../contexts/UserContext";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";

const PAGE =
  "min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50";

const CARD =
  "rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

function getStoredUserId() {
  try {
    const raw = localStorage.getItem("userInfo");
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    return (
      parsed?.userId ||
      parsed?.id ||
      parsed?.user?.userId ||
      parsed?.user?.id ||
      null
    );
  } catch {
    return null;
  }
}

function getNotificationDate(notification) {
  const value =
    notification?.created_at ||
    notification?.createdAt ||
    notification?.createdOn ||
    notification?.date_creation ||
    notification?.date;

  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();

  // Compatible MySQL DATETIME : "2026-05-14 09:32:15"
  const normalized = raw.includes(" ") ? raw.replace(" ", "T") : raw;

  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value) {
  const date =
    value instanceof Date ? value : getNotificationDate({ created_at: value });

  if (!date) return "Date inconnue";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatRelativeDate(notification) {
  const date = getNotificationDate(notification);
  if (!date) return "Date inconnue";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return "À l’instant";
  if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `Il y a ${diffHours} h`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return `Il y a ${diffDays} jours`;

  return formatDateTime(date);
}

function isToday(notification) {
  const date = getNotificationDate(notification);
  if (!date) return false;

  const now = new Date();

  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
}

export default function NotificationList() {
  useScrollToTop();

  const { user } = useUser();
  const { addNotification, refresh } = useNotification();

  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const userId = useMemo(() => {
    return user?.userId || user?.id || getStoredUserId() || 1;
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getNotificationsByUser(userId);
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des notifications :", error);

      addNotification?.(
        "Erreur lors de la récupération des notifications.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [addNotification, userId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const dateA = getNotificationDate(a)?.getTime() || 0;
      const dateB = getNotificationDate(b)?.getTime() || 0;

      return dateB - dateA;
    });
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return sortedNotifications;

    return sortedNotifications.filter((notification) =>
      String(notification?.message || "").toLowerCase().includes(term)
    );
  }, [searchTerm, sortedNotifications]);

  const stats = useMemo(() => {
    const total = notifications.length;
    const today = notifications.filter(isToday).length;
    const last = sortedNotifications[0] || null;

    return {
      total,
      today,
      last,
    };
  }, [notifications, sortedNotifications]);

  const handleDeleteNotification = async (notificationId) => {
    if (!notificationId) return;

    const confirmDelete = window.confirm(
      "Voulez-vous vraiment supprimer cette notification ?"
    );

    if (!confirmDelete) return;

    const previousNotifications = notifications;

    setDeletingId(notificationId);
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== notificationId)
    );

    try {
      await deleteNotificationById(notificationId);

      addNotification?.("Notification supprimée avec succès.", "success");
      refresh?.();
    } catch (error) {
      console.error("Erreur lors de la suppression de la notification :", error);

      setNotifications(previousNotifications);

      addNotification?.(
        "Erreur lors de la suppression de la notification.",
        "error"
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={PAGE}>
      <ScrollIndicator />

      <main className="mx-auto w-full max-w-[1280px] px-3 pb-12 pt-[120px] sm:px-5 md:pt-[145px]">
        <section className="overflow-hidden rounded-[32px] bg-slate-950 shadow-xl dark:bg-slate-900">
          <div className="relative p-5 sm:p-7 lg:p-8">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-indigo-500/25 blur-3xl" />
            <div className="absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-52 w-52 rounded-full bg-blue-500/20 blur-3xl" />

            <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80">
                  <FaBell />
                  Administrateur
                </div>

                <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Notifications
                </h1>

                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
                  Retrouvez les messages envoyés à votre compte, consultez les
                  dernières alertes et supprimez les notifications déjà traitées.
                </p>
              </div>

              <button
                type="button"
                onClick={fetchNotifications}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-950 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSync className={loading ? "animate-spin" : ""} />
                Rafraîchir
              </button>
            </div>

            <div className="relative mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <HeaderStat
                icon={FaInbox}
                label="Total"
                value={stats.total}
                helper="notifications"
              />

              <HeaderStat
                icon={FaCalendarAlt}
                label="Aujourd’hui"
                value={stats.today}
                helper="reçues ce jour"
              />

              <HeaderStat
                icon={FaClock}
                label="Dernière"
                value={stats.last ? formatRelativeDate(stats.last) : "—"}
                helper={
                  stats.last
                    ? formatDateTime(getNotificationDate(stats.last))
                    : "aucune notification"
                }
              />
            </div>
          </div>
        </section>

        <section className={`${CARD} mt-6 overflow-hidden`}>
          <div className="border-b border-slate-200 p-4 dark:border-slate-800 sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Liste des notifications
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {filteredNotifications.length} résultat
                  {filteredNotifications.length > 1 ? "s" : ""} affiché
                  {filteredNotifications.length > 1 ? "s" : ""}.
                </p>
              </div>

              <div className="relative w-full lg:max-w-sm">
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher une notification..."
                  className={`${INPUT} pl-11`}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingState />
          ) : filteredNotifications.length === 0 ? (
            <EmptyState searchTerm={searchTerm} />
          ) : (
            <>
              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-4">Notification</th>
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4 text-right">Action</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredNotifications.map((notification) => (
                      <tr
                        key={notification.id}
                        className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
                      >
                        <td className="px-5 py-4">
                          <NotificationMessage notification={notification} />
                        </td>

                        <td className="whitespace-nowrap px-5 py-4">
                          <div className="text-sm text-slate-800 dark:text-slate-100">
                            {formatRelativeDate(notification)}
                          </div>
                          {/* <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {formatDateTime(getNotificationDate(notification))}
                          </div> */}
                        </td>

                        <td className="px-5 py-4 text-right">
                          <DeleteButton
                            disabled={deletingId === notification.id}
                            onClick={() =>
                              handleDeleteNotification(notification.id)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 gap-3 p-3 lg:hidden">
                {filteredNotifications.map((notification) => (
                  <article
                    key={notification.id}
                    className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50"
                  >
                    <NotificationMessage notification={notification} />

                    <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
                          {formatRelativeDate(notification)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {formatDateTime(getNotificationDate(notification))}
                        </div>
                      </div>

                      <DeleteButton
                        disabled={deletingId === notification.id}
                        onClick={() =>
                          handleDeleteNotification(notification.id)
                        }
                      />
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function HeaderStat({ icon: Icon, label, value, helper }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 text-white shadow-sm backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white">
          <Icon />
        </div>

        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-wide text-white/60">
            {label}
          </div>
          <div className="mt-0.5 truncate text-2xl font-bold">{value}</div>
          <div className="mt-0.5 truncate text-xs text-white/55">{helper}</div>
        </div>
      </div>
    </div>
  );
}

function NotificationMessage({ notification }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
        <FaBell />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-relaxed text-slate-900 dark:text-white">
          {notification?.message || "Notification sans message"}
        </p>

        {notification?.type ? (
          <span className="mt-2 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {notification.type}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function DeleteButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm font-bold text-red-700 transition hover:-translate-y-0.5 hover:border-red-200 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/70"
    >
      <FaTrash />
      Supprimer
    </button>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3 p-4 sm:p-5">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="flex animate-pulse items-center gap-3 rounded-[24px] border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50"
        >
          <div className="h-11 w-11 rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/4 rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-3 w-1/2 rounded-full bg-slate-200 dark:bg-slate-800" />
          </div>
          <div className="hidden h-9 w-28 rounded-2xl bg-slate-200 dark:bg-slate-800 sm:block" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ searchTerm }) {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-2xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FaInbox />
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        {searchTerm ? "Aucun résultat trouvé" : "Aucune notification"}
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        {searchTerm
          ? "Essayez avec un autre mot-clé pour retrouver une notification."
          : "Les notifications envoyées à votre compte apparaîtront ici."}
      </p>
    </div>
  );
}